// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';

import React, { useCallback } from 'react';
import moment from 'moment';
import RGL, { WidthProvider } from 'react-grid-layout';
import Text from 'antd/lib/typography/Text';
import Select from 'antd/lib/select';
import Notification from 'antd/lib/notification';
import { ProjectReport, ProjectReportEntryViewType } from 'cvat-core-wrapper';
import { Col, Row } from 'antd/lib/grid';
import UserReportTable from './views/user-report-table';
import JobReportTable from './views/job-report-table';

const ReactGridLayout = WidthProvider(RGL);

export enum DateIntervals {
    LAST_HOUR = 'last hour',
    LAST_DAY = 'last day',
    LAST_WEEK = 'Last 7 days',
    LAST_MONTH = 'Last 30 days',
    LAST_QUARTER = 'Last 90 days',
    LAST_YEAR = 'Last year',
}

interface Props {
    report: ProjectReport | null;
    onTimePeriodChange: (val: DateIntervals) => void;
}

function ProjectReportOverview(props: Props): JSX.Element | null {
    const { report, onTimePeriodChange } = props;

    if (!report) return null;
    const layout: any = [];
    const views: { view: React.JSX.Element, key: string }[] = [];
    report.statistics.forEach((entry) => {
        /*
            1. 根据typeview生成不同形状的table
            2. 根据typeview取出不同的
        */

        switch (entry.viewType) {
            case ProjectReportEntryViewType.USERTABLE: {
                const { userContent } = entry;
                views.push({
                    view: (
                        <UserReportTable data={userContent} />
                    ),
                    key: entry.name,
                });

                break;
            }
            case ProjectReportEntryViewType.JOBTABLE: {
                const { jobContent } = entry;
                views.push({
                    view: (
                        <JobReportTable data={jobContent} />
                    ),
                    key: entry.name,
                });
                break;
            }
            default: {
                Notification.warning({
                    message: `Cannot display analytics view with view type ${entry.viewType}`,
                });
            }
        }
    });

    // 这个方法放在这个位置主观上感觉不太合适，但是整个页面只有这一个方法，就先放在这里。
    const downloadProjectReport = useCallback((): void => {
        const csvUserReport = report.downloadUserReport();
        const csvJobReport = report.downloadJobReport();
        const csvUserReportBlob = new Blob(csvUserReport, { type: 'text/csv;charset=utf-8' });
        const csvJobReportBlob = new Blob(csvJobReport, { type: 'text/csv;charset=utf-8' });
        const userUrl = URL.createObjectURL(csvUserReportBlob);
        const jobUrl = URL.createObjectURL(csvJobReportBlob);
        const link = document.createElement('a');
        // 感觉这样写很难看
        link.href = userUrl;
        link.setAttribute('download', '用户报告.csv');
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        link.href = jobUrl;
        link.setAttribute('download', '作业报告.csv');
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [report]);
    return (
        // TODO: 后端要传回创建时间，这一部分还没写
        <div className='cvat-analytics-overview'>
            <Row justify='space-between'>
                <Col>
                    <Text type='secondary'>
                        创建于
                        {report.createdDate ? moment(report.createdDate).locale('zh_cn').fromNow() : ''}
                    </Text>
                </Col>
                <Col>
                    <Select
                        placeholder='选择时间区间'
                        defaultValue={DateIntervals.LAST_HOUR}
                        onChange={onTimePeriodChange}
                        options={[
                            {
                                value: DateIntervals.LAST_HOUR,
                                label: '过去 1 小时',
                            },
                            {
                                value: DateIntervals.LAST_DAY,
                                label: '过去 1 天',
                            },
                            {
                                value: DateIntervals.LAST_WEEK,
                                label: '过去 7 天',
                            },
                            {
                                value: DateIntervals.LAST_MONTH,
                                label: '过去 30 天',
                            },
                            {
                                value: DateIntervals.LAST_QUARTER,
                                label: '过去 90 天',
                            },
                            {
                                value: DateIntervals.LAST_YEAR,
                                label: '过去 365 天',
                            },
                        ]}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Text type='secondary'>
                        阶段不为验收的job不统计问题数量与合格率。问题数量与合格率的统计开始时间从job创建时起，与截止时间无关。
                    </Text>
                </Col>
            </Row>
            <ReactGridLayout
                className='layout'
                layout={layout}
                cols={6}
                rowHeight={200}
            >

                { views.map(({ view, key }): JSX.Element => (
                    <div
                        key={key}
                    >
                        { view }
                    </div>
                )) }
            </ReactGridLayout>
            <button onClick={downloadProjectReport} type='button' id='download'>下载表格</button>
        </div>
    );
}
export default ProjectReportOverview;
